import { IconClockPlus } from "@tabler/icons-react"
import { type FC, useState } from "react"

import Tooltip from "@forento/ui/components/Tooltip"

import Button, { SubmitButton } from "~/components/Button"
import { CheckmarkIcon, CloseIcon, CrownIcon, DeleteIcon } from "~/components/Icon"
import InputField from "~/components/InputField"
import Layout, { PageBreadcrumb, PageHeader } from "~/components/Layout"
import { DefaultModal, ModalButtons, ModalInputs, ModalText, ModalTitle } from "~/components/Modal"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import Table from "~/components/Table"
import { useAlert } from "~/contexts/AlertContext"
import { usePlatform, useUser } from "~/contexts/UserContext"
import { toRelativeDateString } from "~/utilities/date"
import routes from "~/utilities/routes"
import trpc, { query } from "~/utilities/trpc"

const TeamSettingsPage: FC = () => {
	const user = useUser()
	const platform = usePlatform()!
	const alert = useAlert()

	const [inviteTeamMemberModal, setInviteTeamMemberModal] = useState<{
		isOpen: boolean
		email: string
		isSubmitting: boolean
	}>({
		isOpen: false,
		email: "",
		isSubmitting: false,
	})
	const [removeTeamMemberModal, setRemoveTeamMemberModal] = useState<{
		isOpen: boolean
		user?: { id: number; fullName: string }
	}>({
		isOpen: false,
	})

	const { data: creators, error, refetch } = query.platform.listCreators.useQuery()

	const closeRemoveTeamMemberModal = () => setRemoveTeamMemberModal(current => ({ ...current, isOpen: false }))

	async function handleSetSuperAdmin(user: { id: number; firstName: string; lastName: string }, value: boolean) {
		const dialog = await alert.confirm(
			value ? "Promote to super admin" : "Remove super admin",
			value
				? `Are you sure you want to promote **${user.firstName} ${user.lastName}** to super admin? They will be able to add and remove team members, including yourself.`
				: `Are you sure you want to demote **${user.firstName} ${user.lastName}** from super admin? They will no longer be able to add and remove team members.`,
			{ confirmLabel: value ? "Promote" : "Demote", isDestructive: !value },
		)
		if (!dialog.result) return

		try {
			await trpc.platform.setCreatorSuperAdmin.mutate({ userId: user.id, value })
			await refetch()
			dialog.close()
		} catch (error) {
			console.error(error)
			await alert.show("Error", "Failed to assign super admin. Please try again.")
		}
	}

	const handleInviteTeamMember = async () => {
		if (inviteTeamMemberModal.email.trim().length === 0) return

		setInviteTeamMemberModal(c => ({ ...c, isSubmitting: true }))

		try {
			await trpc.platform.inviteCreator.mutate({ email: inviteTeamMemberModal.email.trim() })
			setInviteTeamMemberModal({ isOpen: false, email: "", isSubmitting: false })
		} catch (error) {
			console.error(error)
			await alert.show(
				"Error",
				"Failed to invite team member. Make sure you have entered a valid email address and try again.",
			)
			setInviteTeamMemberModal(c => ({ ...c, isSubmitting: false }))
		}
		setInviteTeamMemberModal(c => ({ ...c, isSubmitting: false }))

		await Promise.all([user.reload(), refetch()])
	}

	const handleRemoveTeamMember = async () => {
		if (removeTeamMemberModal.user?.id === undefined) return

		await trpc.platform.removeCreator.mutate(removeTeamMemberModal.user.id)
		closeRemoveTeamMemberModal()
		await refetch()
	}

	const handleExtendInviteExpiryTime = async (email: string) => {
		const prompt = await alert.confirm(
			"Extend invite",
			`Are you sure you want to extend the expiry date of the invite to ${email}?`,
			{ isDestructive: false },
		)
		if (!prompt.result) return

		await trpc.platform.extendCreatorInvite.mutate({ email })
		prompt.close()
		await refetch()
	}

	const handleRemoveInvite = async (email: string) => {
		const prompt = await alert.confirm("Delete invite", `Are you sure you want to delete the invite to ${email}?`)
		if (!prompt.result) return

		await trpc.platform.deleteCreatorInvite.mutate({ email })
		prompt.close()
		await refetch()
	}

	return (
		<Layout>
			<DefaultModal
				onClose={() => setInviteTeamMemberModal(current => ({ ...current, isOpen: false }))}
				isOpen={inviteTeamMemberModal.isOpen}
				onSubmit={handleInviteTeamMember}
			>
				<ModalTitle>Invite team member</ModalTitle>
				{platform.hasReachedAdminsLimit ? (
					<>
						<ModalText>
							You have reached the maximum number of team members. You need to upgrade your plan to invite
							more.
						</ModalText>
						<ModalButtons>
							<Button
								variant="secondary"
								onClick={() => setInviteTeamMemberModal(current => ({ ...current, isOpen: false }))}
								isDisabled={inviteTeamMemberModal.isSubmitting}
							>
								Close
							</Button>
							<Button variant="primary" onClick={routes.settings.billing.plans()}>
								Go to plans
							</Button>
						</ModalButtons>
					</>
				) : (
					<>
						<ModalInputs>
							<InputField
								label="Email address"
								value={inviteTeamMemberModal.email}
								onChange={email => setInviteTeamMemberModal(current => ({ ...current, email }))}
							/>
						</ModalInputs>
						<ModalButtons>
							<Button
								variant="secondary"
								onClick={() => setInviteTeamMemberModal(current => ({ ...current, isOpen: false }))}
								isDisabled={inviteTeamMemberModal.isSubmitting}
							>
								Cancel
							</Button>
							<SubmitButton variant="primary" isLoading={inviteTeamMemberModal.isSubmitting}>
								Invite team member
							</SubmitButton>
						</ModalButtons>
					</>
				)}
			</DefaultModal>

			<DefaultModal
				onClose={closeRemoveTeamMemberModal}
				isOpen={removeTeamMemberModal.isOpen}
				onSubmit={handleRemoveTeamMember}
			>
				<ModalTitle>Remove team member</ModalTitle>
				<ModalText>
					Are you sure you want to remove the team member{" "}
					<strong>{removeTeamMemberModal.user?.fullName}</strong>?
				</ModalText>
				<ModalButtons>
					<Button variant="secondary" onClick={closeRemoveTeamMemberModal}>
						Cancel
					</Button>
					<SubmitButton variant="primary-danger">Remove team member</SubmitButton>
				</ModalButtons>
			</DefaultModal>

			<PageHeader>
				<PageBreadcrumb path={[{ title: "Settings", link: routes.settings.index() }]} title="Team Members" />
				{user.user!.isSuperAdmin ? (
					<Button
						variant="primary"
						onClick={() => setInviteTeamMemberModal(current => ({ ...current, isOpen: true }))}
					>
						Invite team member
					</Button>
				) : (
					<Tooltip tooltip="You don't have permission to invite team members.">
						<Button variant="primary" isDisabled>
							Invite team member
						</Button>
					</Tooltip>
				)}
			</PageHeader>
			{error ? (
				<p>Failed to load team members.</p>
			) : !creators ? (
				<PartialLoadingPage />
			) : (
				<div className="flex flex-col gap-8">
					<Table
						columns={[
							{ label: "Name", flex: { grow: 1, basis: 0 } },
							{ label: "Super admin", flex: { grow: 0, basis: 90 } },
							{ label: "Actions", flex: { grow: 0, basis: 150 } },
						]}
						data={creators.users.map(x => [
							<div className="flex items-center">
								{x.firstName} {x.lastName}
							</div>,
							x.isSuperAdmin ? (
								<CheckmarkIcon className="text-success size-6" />
							) : (
								<CloseIcon className="text-danger size-6" />
							),
							x.id !== user.user!.id && user.user!.isSuperAdmin && (
								<div className="flex items-center gap-2">
									{user.user!.isSuperAdmin &&
										user.user!.id !== x.id &&
										(!x.isSuperAdmin ? (
											<Tooltip tooltip="Promote to super admin" className="flex">
												<button
													type="button"
													onClick={() => handleSetSuperAdmin(x, true)}
													className="cursor-pointer"
												>
													<CrownIcon className="size-6 text-yellow-500" />
												</button>
											</Tooltip>
										) : (
											<Tooltip tooltip="Demote from super admin" className="flex">
												<button
													type="button"
													onClick={() => handleSetSuperAdmin(x, false)}
													className="cursor-pointer"
												>
													<CrownIcon className="text-danger size-6" />
												</button>
											</Tooltip>
										))}
									<Tooltip tooltip="Remove" className="flex">
										<button
											type="button"
											onClick={() =>
												setRemoveTeamMemberModal({
													isOpen: true,
													user: {
														id: x.id,
														fullName: `${x.firstName} ${x.lastName}`,
													},
												})
											}
											className="cursor-pointer"
										>
											<DeleteIcon className="text-danger size-6" />
										</button>
									</Tooltip>
								</div>
							),
						])}
					/>
					{creators.invited.length > 0 && (
						<div>
							<h2 className="mb-4 text-2xl font-semibold">Invited members</h2>
							<Table
								columns={[
									{ label: "Email", flex: { grow: 1, basis: 0 } },
									{ label: "Expires", flex: { grow: 0, basis: 150 } },
									{ label: "Actions", flex: { grow: 0, basis: 150 } },
								]}
								data={creators.invited.map(x => [
									<div className="flex items-center">{x.email}</div>,
									toRelativeDateString(x.expireDate),
									<div className="flex items-center gap-2">
										<Tooltip tooltip="Extend time" className="flex">
											<button
												type="button"
												onClick={() => handleExtendInviteExpiryTime(x.email)}
												className="cursor-pointer"
											>
												<IconClockPlus className="size-6 text-gray-500" />
											</button>
										</Tooltip>
										<Tooltip tooltip="Delete invite" className="flex">
											<button
												type="button"
												onClick={() => handleRemoveInvite(x.email)}
												className="cursor-pointer"
											>
												<DeleteIcon className="text-danger size-6" />
											</button>
										</Tooltip>
									</div>,
								])}
							/>
						</div>
					)}
				</div>
			)}
		</Layout>
	)
}

export default TeamSettingsPage
